<template>
  <CCard>
    <ProcessBarModal
        title="Uploading file on the server"
        :show-modal="showUploadModal"
        :step="uploadStep"
        :file-name="uploadingFileName"
        :progress="uploadProgress"
        :error-text="uploadError"
        :steps-text="{upload: 'Uploading file on the server', inject: 'Creating campaign file', success: 'Created campaign file', error: 'Something went wrong'}"
        @close="() => showUploadModal = false"
    />
    <CCardHeader v-if="!(userData.role === 'SuperUser')">
      <CampaignViewHeader
        title="File loading"
        :selected-member-id="selectedMemberId"
        :member-add-empty="addClearMember"
        :update-member-id-function="updateMemberId"
      />
    </CCardHeader>
    <CCardBody>
      <div v-if="canUpload">
        <CRow>
          <CCol col="4">
            {{ $t("File type") }}:
            <MzSelect
              searchable
              class="mb-3"
              :options="file_types"
              :value.sync="selected_file_type"
            />
          </CCol>
          <CCol col="4">
            <CRow>
              {{ $t("Template to download") }}:
            </CRow>
            <CRow>
              <div
                :class="{'template-file': selected_file_type_template !== '-'}"
                @click="
                  $downloadFile({
                    downloadFileName: selected_file_type_template,
                    url: FILE_TYPES_ENDPOINTS.file_type_download_url(selected_file_type_template)
                  })
                "
              >
                {{ selected_file_type_template }}
              </div>
            </CRow>
          </CCol>
          <CCol col="4">
            <file-dragdrop
              :title="$t('Upload New File')"
              @upload="handleUpload"
              :disabled="!selectedMemberId || loadingPendingFiles || uploadingFiles || closed_campaign"
              :i18nCTA="$t('click or drop files here')"
              :i18nDismiss="$t('Dismiss')"
              :i18nProgress="$t('Upload running...')"
              :i18nImport="$t('Import data for')"
              :i18nDontLeaveMessage="$t('Do not leave this page')"
              :i18nProgressTooltip="
                $t('Please wait until the upload is completed')
              "
            >
              <template #body-content>
                {{ $t("In case you upload the data in a zip file, notice that only zip of one unique file and no folder are authorized") }}
              </template>
              <template #upload-button>
                <CButton
                  color="primary"
                  class="mz-button v2-mz-btn"
                  :disabled="!selectedMemberId || loadingPendingFiles || uploadingFiles || closed_campaign"
                >
                  <DotsLoader v-if="uploadingFiles"/>
                  <span v-else>
                    {{ $t("Upload a file on the plateforme") }}
                  </span>
                </CButton>
              </template>
            </file-dragdrop>
          </CCol>
        </CRow>
        <div v-for="file in pendingFiles" class="mb-5">
          <CRow class="mb-3">
            <CCol col="3">
              <label class="mt-2">{{ $t("File") }}:</label>
            </CCol>
            <CCol col="9">
              <div
                disabled
                class="
                  w-100
                  input-file
                  mz-input
                  bg-white
                  border-primary
                  d-flex
                  justify-content-between
                  align-items-center
                  px-2
                "
                :key="file.file_name"
              >
                <span>
                  {{ file.file_name }}
                </span>
                <CSpinner size="sm" color="primary" v-if="loadingPendingFiles"/>
              </div>
            </CCol>
          </CRow>
          <CRow>
            <CCol col="3">
              <label class="mt-2">{{ $t("File type") }}:</label>
            </CCol>
            <CCol col="8">
              <MzSelect
                style="width: 300px"
                class="mb-3"
                :options="availableFileTypes"
                :value.sync="fileTypes[file.file_name]"
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol col="3">
              <label class="mt-2"> {{ $t("Select the sheet name") }}: </label>
            </CCol>
            <CCol col="9">
              <MzSelect
                style="width: 300px"
                searchable
                class="mb-3"
                :options="sheetNames[file.file_name] || []"
                :value.sync="selectedSheetName[file.file_name]"
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol col="3">
              <label class="mt-2">
                {{ $t("The number of lines in the header") }}:
              </label>
            </CCol>
            <CCol col="9">
              <CInput
                style="width: 300px"
                :min="0"
                addInputClasses="mz-input"
                type="number"
                v-model="skipRows[file.file_name]"
                required
              ></CInput>
            </CCol>
          </CRow>
        </div>
      </div>

      <div v-if="fileUploadResponse && Object.keys(fileUploadResponse).length">
        <template v-for="fileName in Object.keys(fileUploadResponse)">
          {{ fileName }}
          <table-component
            :data="fileUploadResponse[fileName]"
            :columns="['test', 'result']"
            :headings="{ result: $t('Result') }"
          >
          </table-component>
        </template>
      </div>

      <table-component
        ref="filesTable"
        :columns="Object.keys(tableHeadings)"
        :filterExcludeColumns="['actions', 'file_type', 'file_name', 'updated_file', 'date_creation', 'date_modification', 'line_count']"
        :sortExcludeColumns="['actions']"
        :tableDateFormat="$options.dateFormat"
        :headings="tableHeadings"
        forceFilter
        :data="loadedFiles"
        :key="`${selectedMemberId}-${table_counter}-${fileTypeFilter}-${dateFilter}`"
      >
        <template #actions="{ row }" v-if="canUpload">
          <div class="d-flex gap-2 flex-wrap">
            <CButton
              @click="() => deleteFile(row)"
              color="danger"
              variant="outline"
            >
              <i class="fa fa-trash-o"/>
            </CButton>
          </div>
        </template>
        <template #line_count="{ row }">
          {{ $getNumericalValue(row.line_count, 0) }}
        </template>
        <template #file_type="{ row }">
          {{  row[`file_type_${$store.state.language}`] }}
        </template>
        <template #file_name="{ row }">
          <div
            v-if="row.file_name !== 'EMPTY'"
            class="template-file"
          >
            <CButton
              color="primary"
              variant="outline"
              @click="
              $downloadFile({
                downloadFileName: row.file_name,
                url: MEMBERS_ENDPOINTS.download_file(row.id_file)
              })
            "
            >
              {{ row.file_name }}
            </CButton>
          </div>
          <template v-else>
            {{ $t("File updated manually") }}
          </template>
        </template>
        <template #updated_file="{ row }">
          <div class="d-flex gap-2 flex-wrap">
            <CButton
              v-if="[1, 2, 3, 4, 5].includes(row.id_file_type)"
              @click="
                $downloadFile({
                  url: downloadUpdatedFileUrl(row)
                })
              "
              color="primary"
              variant="outline"
            >
              {{ getUpdateFileName(row) }}
            </CButton>
          </div>
        </template>
        <template slot="filter__file_name">
          <CInput
            addInputClasses="mz-input"
            v-model="fileNameFilter"
            :placeholder="`Filter by ${this.$t('Unprocessed file')}`"
          />
        </template>
        <template slot="filter__updated_file">
          <CInput
            addInputClasses="mz-input"
            v-model="fileUpdatedFilter"
            :placeholder="`Filter by ${this.$t('Updated file')}`"
          />
        </template>
        <template slot="filter__file_type">
          <MzSelect
            :value.sync="fileTypeFilter"
            :options="fileTypeOptions"
            class="table-mz-select"
            :single-option-height="24"
          />
        </template>
        <template slot="filter__line_count">
          <input
              class="form-control mz-input"
              placeholder="Filter by Line Count"
              v-model="lineCountFilter"
              type="text"
              onkeypress="return event.charCode != 32"
          />
        </template>
        <template v-for="slot in ['date_creation', 'date_modification']" :slot="`filter__${slot}`">
          <input
              class="form-control mz-input"
              :placeholder="`Filter by ${slot.split('_').join(' ')}`"
              v-model="dateFilter[slot]"
          />
        </template>
      </table-component>
    </CCardBody>
  </CCard>
</template>

<script>
import CampaignViewHeader from "@/components/CampaignViewHeader";
import {CAMPAIGNS_ENDPOINTS} from "@/services/campaigns";
import {MemberService} from "@/services/members";
import {CampaignMixin} from "@/mixins/campaign";
import {ActiveUser} from "@/services/user";
import {FILE_TYPES_ENDPOINTS, FileTypesServices} from "../../services/file_types";
import {MEMBERS_ENDPOINTS} from "../../services/members";
import {ANNEXI_ENDPOINTS} from "../../services/annexi";
import {ANNEXII_ENDPOINTS} from "../../services/annexii";
import {ANNEXIII_ENDPOINTS} from "../../services/annexiii";
import {RISK_FILES_ENDPOINTS} from "../../services/risk_files";
import Task from "@/services/task";
import ProcessBarModal from "@/components/ProcessBarModal.vue";
import moment from "moment/moment";

export default {
  name: "CampaignUpload",
  components: {CampaignViewHeader, ProcessBarModal},
  mixins: [CampaignMixin],
  data() {
    return {
      MEMBERS_ENDPOINTS,
      FILE_TYPES_ENDPOINTS,
      CAMPAIGNS_ENDPOINTS,
      loadingPendingFiles: false,
      uploadingFiles: false,
      availableFileTypes: [],
      sheetNames: {},
      fileTypes: {},
      selectedMemberId: null,
      selectedSheetName: {},
      skipRows: {},
      fileUploadResponse: {},
      canUpload: ActiveUser.hasRole("User"),
      allFiles: [],
      allFilesFull: [],
      file_types: [],
      selected_file_type: "",
      userData: ActiveUser.get(),
      table_counter: 0,
      addClearMember: false,
      fileTypeFilter: 0,
      fileNameFilter: '',
      fileUpdatedFilter: '',
      dateFilter: {},
      lineCountFilter: '',
      showUploadModal: false,
      uploadStep: '',
      uploadProgress: 0,
      uploadError: '',
      uploadingFileName: '',
      campaign_data: {},
      currentTime: new Date(),
      currentFileTypesList: [],
      allowedFileTypesId: [1, 2, 3, 4, 5],
    };
  },
  computed: {
    pendingFiles() {
      return this.allFiles.filter(f => f.file_status === 'uploaded')
    },
    loadedFiles() {
      this.allFiles = this.allFilesFull
      if (this.fileTypeFilter) this.allFiles = this.allFiles.filter(f => f.id_file_type === this.fileTypeFilter);
      if (this.fileNameFilter) this.allFiles = this.allFiles.filter(f => f.file_name && f.file_name.toLowerCase().includes(this.fileNameFilter.toLowerCase()));
      if (this.fileUpdatedFilter) this.allFiles = this.filterByUpdatedFileName(this.fileUpdatedFilter);
      if (this.lineCountFilter) {
        this.allFiles = this.allFiles.filter(
            f => f.line_count.toString().includes(this.lineCountFilter)
        )
      }
      if (Object.keys(this.dateFilter).length) {
        Object.keys(this.dateFilter).forEach(key => {
          if (this.dateFilter[key])
            this.allFiles = this.allFiles.filter(f => this.formatDate(f[key]).includes(this.dateFilter[key]))
        })
      }

      return this.allFiles.filter(f => !f.file_status || f.file_status === 'loaded')
    },
    tableHeadings() {
      return {
        name: this.$t("Member"),
        file_type: this.$t("File type"),
        line_count: this.$t("Number of lines"),
        file_name: this.$t("Unprocessed file"),
        user_creation: this.$t("Injected by"),
        date_creation: this.$t("Create Date"),
        updated_file: this.$t("Updated file"),
        user_modification: this.$t("Edited by"),
        date_modification: this.$t("Update Date"),
        actions: "",
      };
    },
    fileTypeOptions() {
      let options = [{label: "All", value: 0}];
      this.file_types.filter(type => this.currentFileTypesList.includes(type.value)).forEach(file => {
        options.push({
          label: file[`file_type_${this.$store.state.language}`],
          value: file.value
        })
      })
      return options
    },
    selected_file_type_template() {
      return this.file_types.find(file_type => file_type.value === this.selected_file_type)?.template_name || "-"
    },
    closed_campaign() {
      if (this.campaign_data?.campaign?.date_end) {
        return new Date(this.campaign_data.campaign.date_end) < this.currentTime
      }
      return false
    },
  },
  mounted() {
    this.selectedMemberId = this.$store.state.memberId;
    if (ActiveUser.isAdministrator()) this.addClearMember = true;
    this.fetchCampaignData()
    this.fetchFilesTypes()
    this.fetchFilesList();
    this.fetchAvailableFileTypes()
  },
  methods: {
    formatDate(date) {
      return moment(date).format(this.$options.dateFormat);
    },
    async fetchCampaignData() {
      const {data} = await this.$request({
        url: CAMPAIGNS_ENDPOINTS.campaign_taux(this.campaignId)
      })
      this.campaign_data = data
    },
    filterByUpdatedFileName(updatedName) {
      if (updatedName) {
        let filteredFiles = this.allFiles.filter(f => this.allowedFileTypesId.includes(f.id_file_type))
        filteredFiles = this.allFiles.filter(f => {
          const updated_file_name = this.getUpdateFileName(f);
          return updated_file_name && updated_file_name.toLowerCase().includes(updatedName.toLowerCase());
        });
        return filteredFiles;
      }
      return []
    },
    getUpdateFileName(row) {
      if (this.allowedFileTypesId.includes(row.id_file_type)) {
        const name = row.name.split(" ")[0].toLowerCase();
        const file_type = row[`file_type_${this.$store.state.language}`].split(" ").join("_").toLowerCase()
        return `${name}_${file_type}_${this.campaignId}`
      }
      return ''
    },
    downloadUpdatedFileUrl(row) {
      switch (row.file_type_en) {
        case "Risk at the address":
          return RISK_FILES_ENDPOINTS.download_riskfile(row.id_campaign, row.id_member)
        case "Risk at the address CCR":
          return RISK_FILES_ENDPOINTS.download_riskfile(row.id_campaign, row.id_member)
        case "Risk at the address file (CCR format)":
          return RISK_FILES_ENDPOINTS.download_riskfile(row.id_campaign, row.id_member)
        case "Appendix I":
          return ANNEXI_ENDPOINTS.download_annexi(row.id_campaign, row.id_member)
        case "Appendix II":
          return ANNEXII_ENDPOINTS.download_annexii(row.id_campaign, row.id_member)
        case "Appendix III":
          return ANNEXIII_ENDPOINTS.download_annexiii(row.id_campaign, row.id_member)
        default:
          return ''
      }
    },
    updateMemberId(member_id) {
      this.$store.commit("set", ["memberId", member_id]);
      this.selectedMemberId = member_id
      this.fetchFilesList();
    },
    getQuery() {
      let q = {
        id_campaign: this.campaignId,
        id_member: this.selectedMemberId,
        per_page: 1000,
      };

      if (!this.selectedMemberId && ActiveUser.isAdministrator()) {
        delete q.id_member;
      }
      return q
    },
    async fetchFilesList() {
      if (!this.selectedMemberId && !ActiveUser.isAdministrator()) return
      const {data} = await this.$request({
        url: CAMPAIGNS_ENDPOINTS.files,
        query: this.getQuery()
      })
      this.allFiles = data;
      this.allFilesFull = data;
      this.table_counter += 1
      this.currentFileTypesList = [...new Set(this.allFiles.map(file => file.id_file_type))]
    },
    async fetchFilesTypes() {
      const file_types = await FileTypesServices.getFileTypes()
      this.file_types = file_types.map((file_type) => ({
        value: file_type.id_file_type,
        label: file_type[`file_type_${this.$store.state.language}`],
        template_name: file_type.template_name,
        file_type_en: file_type.file_type_en,
        file_type_fr: file_type.file_type_fr,
      }));
      if (this.file_types.length) {
        this.selected_file_type = this.file_types[0].value
      }
    },
    async fetchAvailableFileTypes() {
      const {data} = await this.$request({
        url: CAMPAIGNS_ENDPOINTS.file_types
      });
      this.availableFileTypes = data;
    },
    async handleUpload(files) {
      await Promise.all(Array.from(files).map((file) => this.uploadFile(file)));
    },
    async handleTaskResult(step) {
      this.uploadingFiles = false;
      this.uploadStep = step;
      await this.fetchFilesList();
    },
    handleOnProgress(value) {
      this.uploadProgress = value;
    },
    async uploadFile(file) {
      this.uploadingFiles = true;
      this.uploadError = '';
      this.uploadProgress = 0;
      this.uploadStep = 'upload'
      this.showUploadModal = true;
      this.uploadingFileName = file.name

      const response = await this.$uploadFile({
        fileId: 2,
        file,
        url: MEMBERS_ENDPOINTS.upload_member_campaign_file(
          this.$route.params.campaignId,
          this.selectedMemberId,
          this.selected_file_type
        ),
        onProgress: this.handleOnProgress,
        showAlerts: false
      });
      this.uploadStep = 'inject';

      if (response && !response.error) {
        const task = new Task(response);
        task.on("result", (result) => {
          if (result?.error) {
            this.handleTaskResult('error')
            this.uploadError = result.error
          }
          else this.handleTaskResult('success');
        });

        task.on("error", (error) => {
          this.handleTaskResult('error');
          this.uploadError = error;
        });

        this.$set(this.fileTypes, file.name, this.fileTypes[file.name] || this.availableFileTypes[0])
        this.$set(this.skipRows, file.name, 0);
      }
      else {
        this.uploadingFiles = false;
        this.uploadStep = 'error';
        this.uploadError = response.error
      }
    },
    async deleteFile(file) {
      await MemberService.deleteFile(file.id_file)
      await this.fetchFilesList();
    },
  },
};
</script>

<style lang="scss" scoped>
.input-file ~ .input-file {
  border-top: none !important;
}
.template-file {
  color: blue;
  text-decoration: underline;
  cursor: pointer;
}
</style>
